import usePublicRateCard from "../../hooks/usePublicRateCard";
import { Spinner } from "../ui/Spinner";
import AddOnsSection from "./AddOnsSection";
import AiVoiceRates from "./AiVoiceRates";
import CallCentreRates from "./CallCentreRates";
import CostEstimator from "./CostEstimator";
import CreditsExplainer from "./CreditsExplainer";
import HowBillingWorks from "./HowBillingWorks";
import SmsRates from "./SmsRates";
import { pricingTheme, type PricingTone } from "./pricingTheme";

interface Props {
    tone: PricingTone;
    currency: string;
    plans: Array<{
        id: string;
        displayName: string;
        setupFeePrices?: Array<{ currency: string; amount?: number | null; chatAmount?: number | null; callAmount?: number | null }> | null;
    }>;
    /** Emit schema.org price markup; the dashboard copy of this section is not crawled. */
    structuredData?: boolean;
}

/**
 * Everything charged on top of a plan, for the currency the visitor picked.
 * Each section renders only what the live rate card returns, so the page
 * never quotes a charge the platform does not raise.
 */
export default function UsagePricing({ tone, currency, plans, structuredData = false }: Props) {
    const { card, loading, currency: normalized } = usePublicRateCard(currency);
    const t = pricingTheme(tone);

    if (!card) {
        return loading ? (
            <div className="flex justify-center py-12">
                <Spinner size="lg" tone={tone === "dark" ? "onDark" : "brand"} aria-label="Loading usage prices" />
            </div>
        ) : null;
    }

    return (
        <div id="usage-pricing" className="scroll-mt-24 space-y-8" data-testid="usage-pricing">
            <div className="text-center">
                <h2 className={`text-3xl font-bold tracking-tight ${t.heading}`}>Usage pricing</h2>
                <p className={`mx-auto mt-3 max-w-2xl text-base ${t.body}`}>
                    What you pay on top of your plan for calls, agents and messages — and exactly how it is collected.
                </p>
            </div>
            <HowBillingWorks tone={tone} card={card} />
            <AiVoiceRates tone={tone} card={card} />
            <CallCentreRates tone={tone} card={card} />
            <SmsRates tone={tone} card={card} structuredData={structuredData} />
            <CreditsExplainer tone={tone} card={card} />
            <CostEstimator tone={tone} card={card} />
            <AddOnsSection tone={tone} currency={normalized} plans={plans} />
        </div>
    );
}
